import { Box, BoxProps, Image, ImageProps, Skeleton } from '@chakra-ui/react';
import React, { ReactElement, useRef, useEffect, useState } from 'react';

type Props = {
  src: string;
  alt: string;
  width?: BoxProps['width'];
  height?: BoxProps['height'];
  objectFit?: ImageProps['objectFit'];
  borderRadius?: BoxProps['borderRadius'];
  fallbackSrc?: string;
  loading?: 'lazy' | 'eager';
  onLoad?: () => void;
  onError?: () => void;
};

export const LazyImage = ({
  src,
  alt,
  width = '100%',
  height = '100%',
  objectFit = 'cover',
  borderRadius = 'none',
  fallbackSrc,
  loading = 'lazy',
  onLoad,
  onError,
}: Props): ReactElement => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (loading === 'eager') {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px', // Start loading 50px before the image comes into view
        threshold: 0.1,
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [loading]);

  const handleLoad = (): void => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = (): void => {
    setHasError(true);
    onError?.();
  };

  return (
    <Box
      ref={imgRef}
      width={width}
      height={height}
      position="relative"
      borderRadius={borderRadius}
      overflow="hidden"
    >
      {/* Skeleton placeholder */}
      {!isLoaded && !hasError && (
        <Skeleton width="100%" height="100%" position="absolute" top="0" left="0" zIndex="1" />
      )}

      {/* Actual image */}
      {isInView && (
        <Image
          src={hasError ? fallbackSrc : src}
          alt={alt}
          width="100%"
          height="100%"
          objectFit={objectFit}
          onLoad={handleLoad}
          onError={handleError}
          style={{
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
          }}
        />
      )}
    </Box>
  );
};
